"use client";

import CustomNavLink from "@/components/CustomLink/CustomNavLink";
import { useDictionary } from "@/components/DictProvider/DictProvider";
import usePost from "@/hooks/usePost";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

const DesktopHeader = ({ params }) => {
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState(null);
  const [subCategories, setSubCategories] = useState(null);
  const { PostMethod, loading } = usePost();
  const router = useRouter();
  const pathname = usePathname();
  const currentPath = pathname;
  const dict = useDictionary();

  const dropdownRef = useRef(null); // Ref for the dropdown
  const menuItemRef = useRef(null); // Ref for the menu item

  const menuItems = [
    { title: dict.headerText.categories, key: "Categories" },
    { title: dict.headerText.bestBrands, key: "Best brands" },
    { title: dict.headerText.newArrivals, key: "New arrivals" },
    { title: dict.headerText.bestSellers, key: "Best sellers" },
    { title: dict.headerText.Spotlight, key: "Spotlight" },
  ];

  const toggleDropdown = () => {
    setDropdownVisible((prev) => !prev);
  };

  const handleNavigation = (e, path) => {
    e.preventDefault();
    if (path === "Best brands") {
      path = "bestBrand";
    } else if (path === "New arrivals") {
      path = "newArrival";
    } else if (path === "Best sellers") {
      path = "bestSeller";
    } else if (path === "Spotlight") {
      path = "spotlight";
    }

    if (currentPath !== "/en" || currentPath !== "/ar") {
      router.push(`/${pathname?.split("/")[1]}/#${path}`); // Navigate to the home page with the hash
    } else {
      document
        .getElementById(`${path}`)
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const getSubCategory = async (slug, lang) => {
    try {
      let response = await PostMethod({
        method: "get",
        url: `catSubcat/subcategory/${slug}`,
        language: lang,
      });

      if (response?.success === true) {
        if (response?.data?.length > 0) {
          setSubCategories(response?.data);

          return;
        }

        router.push(`/category/${slug?.toLowerCase()}`);
        toggleDropdown();
      }
    } catch (error) {
      // On API failure, still navigate to category page
      router.push(`/category/${slug?.toLowerCase()}`);
      toggleDropdown();
      return;
    }
  };

  const getCategory = async () => {
    try {
      let response = await PostMethod({
        method: "get",
        url: `catSubcat/category`,
        language: params?.lang,
      });

      if (response?.success === true) {
        setCategories(response?.data);
        setCategory(response?.data[0]?.categorySlug);
        getSubCategory(response?.data[0]?.categorySlug, params?.lang);
        return;
      }
    } catch (error) {
      toast.error(error?.message || "Something Went Wrong");
      return;
    }
  };

  const subCategoryHandler = (slug) => {
    setCategory(slug);
    getSubCategory(slug, params?.lang);
  };

  useEffect(() => {
    getCategory();
    // eslint-disable-next-line
  }, [params?.lang]);


  useEffect(() => {
    if (isDropdownVisible) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isDropdownVisible]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        menuItemRef.current &&
        !menuItemRef.current.contains(event.target)
      ) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div>
      <div className="flex items-center w-full">
        <ul className="flex gap-[24px] font-open_Sans text-[16px] leading-[21px] font-[400] text-[#FFFFFF]">
          {menuItems?.map((item) => (
            // when user click on this li tag then listener should not work 
            <li key={item?.key} className="relative" ref={item.key === "Categories" ? menuItemRef : null}>
              <p
                className="cursor-pointer flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent event bubbling
                  item.key === "Categories" ? toggleDropdown() : handleNavigation(e, item.key);
                }}
              >
                {item?.title}
                {item?.key === "Categories" && (
                  <span>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`transition-transform duration-300 ${isDropdownVisible ? "rotate-180" : ""
                        }
                       `}
                    >
                      <path
                        d="M12 16.5L4.5 9L5.55 7.95L12 14.4L18.45 7.95L19.5 9L12 16.5Z"
                        fill="white"
                      />
                    </svg>
                  </span>
                )}
              </p>
            </li>
          ))}
        </ul>
      </div>

      {isDropdownVisible && categories?.length > 0 && (
        // when user click outside of this div then listener should work make setDropdownVisible is false
        <div ref={dropdownRef}>
          {/* Main Dropdown Menu */}
          <div className="w-full ">
            <div className="p-[24px] pr-1 h-[calc(100vh-148px)]  absolute ltr:left-0 rtl:right-0 top-[49px] bg-[#FCFCFC]  shadow-md  border-[1px] border-[#0000001F] w-[28%] z-[999]">
              <ul className="overflow-y-auto w-full h-full font-open_Sans flex flex-col gap-[20px] mt-2">
                {categories?.map((item) => (
                  <li key={item?._id}>
                    <p
                      onClick={() => subCategoryHandler(item?.categorySlug)}
                      className={`${category === item?.categorySlug
                        ? "bg-[#F3EBDF] text-[#000000]"
                        : ""
                        } px-[6px] ltr:mr-4 rtl:ml-4 flex justify-between hover:bg-[#F3EBDF] hover:text-[#000000] rounded-[6px] h-[36px] items-center text-[#00000099] text-[16px] font-normal leading-[21px] cursor-pointer`}
                    >
                      {item?.category}
                      {category === item?.categorySlug && (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className={`transition-transform duration-300 rtl:rotate-180 `}
                        >
                          <path
                            d="M16.5 12L9 19.5L7.95 18.45L14.4 12L7.95 5.55L9 4.5L16.5 12Z"
                            fill="black"
                            fillOpacity="0.8"
                          />
                        </svg>
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {/* Sub Dropdown Menu */}

          <div className="w-full h-full">
            <div className="flex p-[24px] pr-1 h-[calc(100vh-148px)]  shadow-md border-[1px] border-[#0000001F] bg-[#FCFCFC] absolute rtl:left-0 ltr:right-0 top-[49px] w-[72%] z-[999]">
              <ul
                className={`grid place-content-start place-items-start ${subCategories?.length > 6 ? "grid-cols-2" : "grid-cols-1"
                  } gap-x-[20px] gap-y-[44px] overflow-y-auto w-full font-open_Sans duration-300 ease-in-out`}
              >
                {subCategories
                  ?.reduce((resultArray, item, index) => {
                    const chunkIndex = Math.floor(index / 6);
                    if (!resultArray[chunkIndex]) {
                      resultArray[chunkIndex] = []; // start a new chunk
                    }
                    resultArray[chunkIndex]?.push(item);
                    return resultArray;
                  }, [])
                  .map((chunk, chunkIndex) => (
                    <div className="flex flex-col gap-[12px]" key={chunkIndex}>
                      {chunk?.map((item) => (
                        <li
                          key={item?.subCategory}
                          onClick={() => {
                            toggleDropdown();
                          }}
                        >
                          <CustomNavLink
                            href={`/category/${category?.toLowerCase()}?category=${item?.subCategory
                              ?.replace(" ", "+")
                              ?.replace("&", "%26")}`}
                            className={`flex gap-[6px] items-center text-primary underline text-[16px] leading-[21px] cursor-pointer`}
                          >
                            {item?.subCategory}
                          </CustomNavLink>
                        </li>
                      ))}
                    </div>
                  ))}
              </ul>

              <svg
                onClick={toggleDropdown}
                className="transition-all absolute top-8 ltr:right-8 rtl:left-12 duration-300 ease-in-out cursor-pointer"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 19L12 12M12 12L19 5M12 12L5 5M12 12L19 19"
                  stroke="black"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              {loading && (
                <div className="absolute w-full h-full inset-0 flex justify-center items-center bg-white/50">
                  <span className="loader"></span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopHeader;
